/**
 * providerOnboardingVocabulary.js — the words for the admin provider-onboarding preparation surface.
 *
 * The surface displays nine closed sets that the SERVER owns: the three preview verdicts, the three
 * finding severities, the twenty-two finding reasons, the three registry states, the four credential
 * states, the seven boundaries, the seven draft field names, the three data classes and the eight
 * capability tokens, plus the six authorities an onboarding preparation does not carry. This module
 * holds the Hebrew for each arm and nothing else — no option list, no order, no default.
 *
 * That split is the point, and it is the same one `skillReviewVocabulary` makes. Every option list,
 * every order and every arm that is still live comes from `GET /api/connections/provider-onboarding/
 * contract` at render time. A client that hardcoded them would keep offering an arm the preview has
 * renamed — and on THIS surface a stale arm would be a field name, a refusal reason or a boundary, so
 * a renamed refusal would print as a blank cell beside a draft an operator is being asked to send to
 * the provisioning step.
 *
 * What this module must never acquire is a word for something that is not a fact of the preview. The
 * preview provisions nothing, enables nothing and returns no secret value, so there is no arm here for
 * adding, connecting, testing, enabling or activating a provider — and the activation sentences below
 * are all statements that the preparation does NOT do.
 *
 * Two lists on the answer are deliberately treated differently, and the difference is not arbitrary:
 *
 *   * `boundaries` are closed TOKENS (`no_secret_disclosure`), so each gets a Hebrew arm here and the
 *     parity test pins the key set against the server's tuple in both directions.
 *   * `caveats` are free PROSE the preview authors (`"the preview validates the shape of a draft, not
 *     the provider's behaviour"`). They are printed from the wire, verbatim, and this module holds no
 *     copy of any of them. Re-typing a sentence here would be a second source of truth for a claim
 *     that is not a token: a server edit would leave the card printing the old wording about what the
 *     preview cannot see, and nothing would go red. The parity test pins that absence too.
 *
 * `providerOnboardingVocabulary.parity.test.js` reads the backend modules as text and pins every key
 * set in both directions, so an arm added or renamed on the server reds this module rather than
 * silently printing a Hebrew word for it.
 */

/** Every resolver's answer for a value outside its scale, and for anything that is not a value. */
export const PROVIDER_ONBOARDING_UNKNOWN_HE = "לא מזוהה";

// ─── The three preview verdicts ────────────────────────────────────────────────
//
// `ready_for_operator_provisioning` is a verdict about a DRAFT, not about a provider: it says the
// draft is well-formed enough to be handed to the operator-gated provisioning step. The word says so,
// because "מוכן" alone on this card would read as "the provider is ready".

export const PROVIDER_ONBOARDING_VERDICT_HE = {
  ready_for_operator_provisioning: "הטיוטה תקינה — מוכנה להעברה לשלב ההקצאה התפעולית",
  incomplete: "הטיוטה חסרה — יש שדות שאינם מלאים",
  refused: "הטיוטה נדחתה — יש שדות שאינם קבילים",
};

// ─── The three finding severities ──────────────────────────────────────────────

export const PROVIDER_ONBOARDING_SEVERITY_HE = {
  refusal: "דחייה",
  incomplete: "חוסר",
  disclosure: "לידיעה",
};

// ─── The twenty-two finding reasons, one word per arm ─────────────────────────
//
// Keyed by token only. The ORDER findings are shown in is the server's (severity, then field, then
// reason), and the verdict is the most blocking severity present — both are facts about the draft, not
// something a client may decide. `providerOnboardingFindingLabels` therefore preserves input order.

export const PROVIDER_ONBOARDING_REASON_HE = {
  // the draft is not filled in yet
  field_absent: "השדה לא נשלח",
  field_placeholder_unresolved: "השדה עדיין מכיל את ממלא המקום של התבנית",
  // the value is not usable
  field_not_permitted_chars: "השדה מכיל תו שאינו מותר",
  field_too_long: "השדה ארוך מהמותר",
  field_secret_shaped: "השדה נראה כמו סוד — אין לשלוח סודות בטופס הזה",
  list_entry_duplicated: "אותו ערך מופיע יותר מפעם אחת",
  declared_model_not_recognised: "המודל המוצהר אינו מזוהה",
  data_class_not_declared: "לא הוצהר סיווג נתונים",
  data_class_not_recognised: "סיווג הנתונים אינו מזוהה",
  capability_not_recognised: "יכולת שאינה מזוהה",
  capability_local_private_requires_local_data_class:
    "יכולת local_private מחייבת סיווג נתונים local",
  endpoint_scheme_invalid: "כתובת ה-endpoint חייבת להיות https",
  endpoint_carries_userinfo: "כתובת ה-endpoint מכילה פרטי משתמש",
  endpoint_carries_query_or_fragment: "כתובת ה-endpoint מכילה שאילתה או עוגן",
  endpoint_host_invalid: "שם המארח בכתובת ה-endpoint אינו תקין",
  credential_value_refused: "התקבל ערך סודי במקום הפניה — הערך לא נשמר ולא הוחזר",
  credential_reference_malformed: "הפנית הסוד אינה כתובה בתחביר המוכר",
  credential_reference_namespace_unresolvable: "מרחב השמות של ההפניה אינו ניתן לפתרון",
  credential_reference_env_disabled: "הפניית env אינה זמינה בהגדרות השרת",
  // not a defect: something the operator should know before sending
  provider_already_registered: "הספק כבר רשום במערכת",
  provider_not_yet_registered: "הספק טרם רשום במערכת",
  credential_secret_ref_absent: "לא צוינה הפניית סוד",
};

// ─── The three registry states ────────────────────────────────────────────────

export const PROVIDER_ONBOARDING_REGISTRY_STATE_HE = {
  registered: "רשום במערכת",
  unregistered: "אינו רשום במערכת",
  undeclared: "לא הוצהר מזהה ספק",
};

// ─── The four credential states ───────────────────────────────────────────────
//
// None of these four says a credential WORKS. `reference_usable` says the reference parses and its
// namespace is one the server knows; whether the secret behind it resolves is decided at resolve time.

export const PROVIDER_ONBOARDING_CREDENTIAL_STATE_HE = {
  not_supplied: "לא צוינה הפניית סוד",
  reference_usable: "הפניה תקינה לתחביר — הסוד עצמו לא נפתר ולא נבדק",
  reference_unresolvable: "ההפניה אינה ניתנת לפתרון",
  value_refused: "נשלח ערך סודי במקום הפניה — הערך לא נשמר ולא הוחזר",
};

// ─── The seven boundaries ─────────────────────────────────────────────────────

export const PROVIDER_ONBOARDING_BOUNDARY_HE = {
  no_credential_provisioning: "לא הוקצה סוד",
  no_secret_disclosure: "לא הוחזר ערך סודי",
  no_provider_enablement: "הספק לא הופעל",
  no_connection_test: "לא נבדק חיבור לספק",
  no_routing_change: "לא שונתה מדיניות הניתוב",
  no_tool_permission_grant: "לא הוענקה הרשאת כלי",
  no_registry_write: "לא נכתב דבר לרישום הספקים",
};

// ─── The six authorities a preparation does not carry ─────────────────────────
//
// Keyed by the server's own `activation` block, every boolean of which is False on every answer. Each
// sentence is the negative of one key, so the card can print what the preparation did NOT do without
// the surface asserting it in its own words — the block travels on the wire and this module only
// words it. `state` is not here: it is a token, worded by `providerOnboardingActivationStateLabel`.

export const PROVIDER_ONBOARDING_ACTIVATION_HE = {
  provider_enabled: "הספק לא הופעל",
  secrets_provisioned: "לא הוקצה סוד",
  connection_verified: "החיבור לספק לא אומת",
  route_changed: "מדיניות הניתוב לא שונתה",
  authority_granted: "לא הוענקה הרשאה",
  audit_written: "לא נכתב דבר ליומן הביקורת",
};

/** The one activation state the server declares. Anything else is named as unknown, never printed. */
export const PROVIDER_ONBOARDING_ACTIVATION_STATE_HE = {
  operator_gated: "מותנה בפעולה תפעולית — הטופס הזה אינו מקצה סוד ואינו מפעיל ספק",
};

// ─── The seven draft fields the form collects ─────────────────────────────────

export const PROVIDER_ONBOARDING_FIELD_HE = {
  provider_id: "מזהה ספק",
  display_name: "שם תצוגה",
  endpoint: "כתובת ה-endpoint",
  credential_secret_ref: "הפניית סוד",
  declared_models: "מודלים מוצהרים",
  data_class: "סיווג נתונים",
  capabilities: "יכולות",
};

/**
 * The sentence that has to sit NEXT TO the credential field. The field accepts a reference and the
 * preview refuses a value, so an operator who pasted a key has to be told the value was discarded
 * rather than stored — and told before they paste, not after.
 */
export const PROVIDER_ONBOARDING_CREDENTIAL_REF_NOTE_HE =
  "יש למלא הפניה (למשל env:NAME או kv:path/name) ולא את הסוד עצמו. ערך סודי שנשלח נדחה ואינו נשמר.";

/** The heading over the verbatim block, so the English sentences are labelled as the server's own. */
export const PROVIDER_ONBOARDING_CAVEATS_NOTE_HE =
  "מה התצוגה הזו אינה בודקת — בלשון השרת:";

// ─── The two choice vocabularies the form offers ──────────────────────────────

export const PROVIDER_ONBOARDING_DATA_CLASS_HE = {
  cloud: "ענן",
  local: "מקומי",
  unknown: "לא הוצהר",
};

export const PROVIDER_ONBOARDING_CAPABILITY_HE = {
  reasoning: "הנמקה",
  coding: "קוד",
  vision: "ראייה",
  voice: "קול",
  long_context: "הקשר ארוך",
  tool_use: "שימוש בכלים",
  fast_classification: "סיווג מהיר",
  local_private: "מקומי ופרטי",
};

// ─── Resolvers ─────────────────────────────────────────────────────────────────
//
// Every resolver NAMES a value it does not know instead of echoing it. A bare `MAP[value]` lookup
// also answers `constructor`, `toString` and `__proto__` with an inherited member, which would print
// a function's source into a Hebrew badge, so the lookup is guarded by `hasOwnProperty`.

function word(map, value) {
  if (typeof value !== "string" || !Object.prototype.hasOwnProperty.call(map, value)) {
    return PROVIDER_ONBOARDING_UNKNOWN_HE;
  }
  return map[value];
}

export function providerOnboardingVerdictLabel(value) {
  return word(PROVIDER_ONBOARDING_VERDICT_HE, value);
}

export function providerOnboardingSeverityLabel(value) {
  return word(PROVIDER_ONBOARDING_SEVERITY_HE, value);
}

export function providerOnboardingReasonLabel(value) {
  return word(PROVIDER_ONBOARDING_REASON_HE, value);
}

export function providerOnboardingRegistryStateLabel(value) {
  return word(PROVIDER_ONBOARDING_REGISTRY_STATE_HE, value);
}

export function providerOnboardingCredentialStateLabel(value) {
  return word(PROVIDER_ONBOARDING_CREDENTIAL_STATE_HE, value);
}

export function providerOnboardingBoundaryLabel(value) {
  return word(PROVIDER_ONBOARDING_BOUNDARY_HE, value);
}

export function providerOnboardingActivationStateLabel(value) {
  return word(PROVIDER_ONBOARDING_ACTIVATION_STATE_HE, value);
}

export function providerOnboardingFieldLabel(value) {
  return word(PROVIDER_ONBOARDING_FIELD_HE, value);
}

export function providerOnboardingDataClassLabel(value) {
  return word(PROVIDER_ONBOARDING_DATA_CLASS_HE, value);
}

export function providerOnboardingCapabilityLabel(value) {
  return word(PROVIDER_ONBOARDING_CAPABILITY_HE, value);
}

/** The server's own ordered boundary list, worded. Input order is the server's and is preserved. */
export function providerOnboardingBoundaryLabels(values) {
  if (!Array.isArray(values)) return [];
  return values.map(providerOnboardingBoundaryLabel);
}

/**
 * The findings the server returned, worded as one row each.
 *
 * `value` keeps the server's exact tokens on screen next to the word, because the tokens are what the
 * API and the logs call the field and the reason. A finding whose field or reason this module has no
 * word for still renders: it shows the raw token, so a reason added on the server appears as an
 * unworded token rather than as a silent gap in the list an operator is reading.
 */
export function providerOnboardingFindingLabels(findings) {
  if (!Array.isArray(findings)) return [];
  return findings.map((finding) => {
    const field = typeof finding?.field === "string" ? finding.field : "";
    const reason = typeof finding?.reason === "string" ? finding.reason : "";
    const severity = typeof finding?.severity === "string" ? finding.severity : "";
    return {
      field,
      reason,
      severity,
      fieldLabel: field ? providerOnboardingFieldLabel(field) : PROVIDER_ONBOARDING_UNKNOWN_HE,
      reasonLabel: reason ? providerOnboardingReasonLabel(reason) : PROVIDER_ONBOARDING_UNKNOWN_HE,
      severityLabel: severity
        ? providerOnboardingSeverityLabel(severity)
        : PROVIDER_ONBOARDING_UNKNOWN_HE,
    };
  });
}

/**
 * The activation block as sentences.
 *
 * Only a key whose value is EXACTLY `false` is worded. A key that is absent, or that carries anything
 * else — `true`, `"false"`, `0` — is reported as unknown rather than silently dropped: a card that hid
 * an authority it did not recognise would be the one place where "nothing was enabled" could stop
 * being true without anything on screen changing.
 */
export function providerOnboardingActivationSentences(activation) {
  if (!activation || typeof activation !== "object") return [];
  const out = [];
  for (const key of Object.keys(PROVIDER_ONBOARDING_ACTIVATION_HE)) {
    const value = Object.prototype.hasOwnProperty.call(activation, key)
      ? activation[key]
      : undefined;
    if (value === false) out.push(PROVIDER_ONBOARDING_ACTIVATION_HE[key]);
    else out.push(`${key}: ${PROVIDER_ONBOARDING_UNKNOWN_HE}`);
  }
  return out;
}

/**
 * One server flag rendered as a fact, WITHOUT collapsing "not reported" into "no".
 *
 * Used for `credential.value_returned` and `registry.declarations_verified`, both of which the server
 * sends as a constant `false`. `value ? … : …` would be the wrong test twice over: it would print the
 * negative for a key that was never sent, and it would print a positive-looking Hebrew word for the
 * string `"false"`. Only the strict `false` earns the claim.
 */
export function providerOnboardingFalseFlag(value, whenFalseHe) {
  return value === false ? whenFalseHe : PROVIDER_ONBOARDING_UNKNOWN_HE;
}
